export enum ArtifactType {
  TEXT = 'text',
  CODE = 'code',
  IMAGE = 'image',
  IDEA = 'idea',
  CRITIQUE = 'critique',
  SOLUTION = 'solution',
  DOCUMENT = 'document',
  DIAGRAM = 'diagram',
  JSON = 'json',
  YAML = 'yaml',
  TOML = 'toml',
  CSV = 'csv',
  PROMPT = 'prompt',
  // Added 2026-04-23 for the SendTo→New Artifact flow. MARKDOWN is the
  // simple-text fallback (rendered via ReactMarkdown). PERSONA_CHAT
  // stores the full structured conversation so the dedicated viewer
  // can replay each message with its phase bubble intact.
  MARKDOWN = 'markdown',
  PERSONA_CHAT = 'persona_chat',
  GEOJSON = 'geojson',
  // Backend-emitted typed artifacts produced via the create_artifacts /
  // consult_peers / handoff / pulse pipelines. Their content is jsonb
  // keyed by `markdown` (rich body) or similar; without explicit FE
  // routing they fell through to TextArtifactViewer's JSON.stringify
  // branch on the artifact-detail page. See backend/app/schemas/artifact.py
  // for the canonical 17-type set.
  DECISION_LOG = 'decision_log',
  PULSE_DIGEST = 'pulse_digest',
  HANDOFF_NOTE = 'handoff_note',
  MINDMAP = 'mindmap',
  PROJECT_MANIFEST = 'project_manifest',
  // 2026-05-16: backend emits `type: "mermaid"` for create_artifacts
  // calls with mermaid_code payloads (see backend/app/schemas/artifact.py).
  // Without an enum member here, the type-based dispatch in
  // ArtifactTileBody fell through to DocumentArtifactViewer's default
  // case which dumped the JSON content as text instead of rendering
  // the diagram.
  MERMAID = 'mermaid',
}

export enum ExportFormat {
  JSON = 'json',
  MARKDOWN = 'markdown',
  PDF = 'pdf',
  HTML = 'html',
  PNG = 'png',
  SVG = 'svg',
  DOCX = 'docx',
  ZIP = 'zip'
}

export enum ArtifactRelationshipType {
  DERIVED_FROM = 'derived_from',
  REFERENCES = 'references',
  INSPIRES = 'inspires',
  CRITIQUES = 'critiques',
  IMPLEMENTS = 'implements',
  DEPENDS_ON = 'depends_on',
  SUPERSEDES = 'supersedes'
}

export interface ArtifactRelationship {
  id: string;
  source_id: string;
  target_id: string;
  type: ArtifactRelationshipType;
  description?: string;
  strength?: number;
  created_at: string;
  created_by: string;
  metadata?: Record<string, any>;
}

export interface ArtifactVersion {
  id: string;
  artifact_id: string;
  version_number: number;
  content: any;
  metadata?: Record<string, any>;
  agent_id: string;
  agent_name: string;
  change_description: string;
  created_at: string;
}

export interface ArtifactSummary {
  id: string;
  name: string;
  phase: string | null;
  type: ArtifactType;
  created_at: string;
  updated_at: string;
  version: number;
  agent_name: string;
  thumbnail_url?: string;
  description?: string;
  // Trailmap L1 (plan Task 10) — bind artifact to its origin turn.
  // NULL → Orphan Tray per DC3 (no source turn to attach to).
  // source_trail_id is denormalized at write-time per spec §6.4 to
  // save a JSONB walk per artifact-tile render.
  source_turn_id?: string | null;
  source_trail_id?: string | null;
  // Provenance (firecrawl + any producer writing <ns>.last_meta): surfaced on the card.
  metadata?: Record<string, any>;
}

export interface Artifact extends ArtifactSummary {
  session_id: string;
  content: any;
  metadata: Record<string, any>;
  tags: string[];
  relationships?: ArtifactRelationship[];
}

export interface ArtifactFilter {
  phase?: string;
  type?: ArtifactType;
  agentId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  tags?: string[];
}

export interface ExportResult {
  url?: string;
  filename?: string;
  content?: string;
  success: boolean;
  message?: string;
}
